import { ChangeDetectionStrategy, Component, computed, signal } from '@angular/core';

interface Cell { mine: boolean; adj: number; rev: boolean; flag: boolean; }

interface Level { label: string; cols: number; rows: number; mines: number; cell: number; }

type LevelId = 'beginner' | 'intermediate' | 'expert';

type GameState = 'play' | 'lost' | 'won';

const MS_LEVELS: Record<LevelId, Level> = {
  beginner: { label: 'Beginner', cols: 9, rows: 9, mines: 10, cell: 30 },
  intermediate: { label: 'Intermediate', cols: 16, rows: 16, mines: 40, cell: 24 },
  expert: { label: 'Expert', cols: 24, rows: 16, mines: 99, cell: 20 },
};

const NUM_COLOR = ['', '#7dd3fc', '#86efac', '#fca5a5', '#c4b5fd', '#fcd34d', '#67e8f9', '#e5e7eb', '#9ca3af'];

function blank(lv: LevelId): Cell[] {
  return Array.from({ length: MS_LEVELS[lv].cols * MS_LEVELS[lv].rows }, () => ({ mine: false, adj: 0, rev: false, flag: false }));
}

/**
 * Minesweeper — three difficulty levels, first-click-safe placement, flood reveal
 * and right-click flags. Ported from the OTA-B2B-6 design (games.jsx). Default
 * (Emulated) encapsulation keeps its `ms-*` cell CSS scoped — no clash with the
 * Entertainment Minesweeper.
 */
@Component({
  selector: 'app-mini-minesweeper',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './mini-minesweeper.component.html',
  styleUrl: './mini-minesweeper.component.scss',
})
export class MiniMinesweeperComponent {
  readonly levels = Object.entries(MS_LEVELS).map(([id, v]) => ({ id: id as LevelId, label: v.label, sub: `${v.cols}×${v.rows}·${v.mines}` }));
  readonly numColor = NUM_COLOR;

  readonly level = signal<LevelId>('beginner');
  readonly cells = signal<Cell[]>(blank('beginner'));
  readonly state = signal<GameState>('play');
  private started = false;

  readonly cfg = computed<Level>(() => MS_LEVELS[this.level()]);
  readonly flags = computed(() => this.cells().filter((x) => x.flag).length);
  readonly minesLeft = computed(() => Math.max(0, this.cfg().mines - this.flags()));
  readonly cell = computed(() => {
    const cfg = this.cfg();
    return Math.max(13, Math.min(cfg.cell, Math.floor((484 - (cfg.cols - 1) * 3 - 16) / cfg.cols)));
  });
  readonly gridCols = computed(() => `repeat(${this.cfg().cols}, ${this.cell()}px)`);
  readonly fontSize = computed(() => this.cell() * 0.52);

  private neighbors(k: number): number[] {
    const cfg = this.cfg();
    const r = Math.floor(k / cfg.cols);
    const c = k % cfg.cols;
    const out: number[] = [];
    for (let dr = -1; dr <= 1; dr++) {
      for (let dc = -1; dc <= 1; dc++) {
        if (!dr && !dc) continue;
        const nr = r + dr, nc = c + dc;
        if (nr >= 0 && nr < cfg.rows && nc >= 0 && nc < cfg.cols) out.push(nr * cfg.cols + nc);
      }
    }
    return out;
  }

  private placeMines(base: Cell[], safe: number): Cell[] {
    const cfg = this.cfg();
    const banned = new Set([safe, ...this.neighbors(safe)]);
    const pool: number[] = [];
    for (let i = 0; i < base.length; i++) if (!banned.has(i)) pool.push(i);
    for (let i = pool.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [pool[i], pool[j]] = [pool[j], pool[i]];
    }
    const next = base.map((x) => ({ ...x, mine: false }));
    for (let k = 0; k < cfg.mines; k++) next[pool[k]].mine = true;
    for (let i = 0; i < next.length; i++) next[i].adj = this.neighbors(i).filter((j) => next[j].mine).length;
    return next;
  }

  private flood(board: Cell[], k: number): void {
    const stack = [k];
    while (stack.length) {
      const i = stack.pop()!;
      const cell = board[i];
      if (cell.rev || cell.flag) continue;
      cell.rev = true;
      if (cell.adj === 0 && !cell.mine) for (const j of this.neighbors(i)) if (!board[j].rev) stack.push(j);
    }
  }

  click(k: number): void {
    const cells = this.cells();
    if (this.state() !== 'play' || cells[k].flag || cells[k].rev) return;
    let board = cells.map((x) => ({ ...x }));
    if (!this.started) { board = this.placeMines(board, k); this.started = true; }
    if (board[k].mine) {
      board.forEach((x) => { if (x.mine) x.rev = true; });
      this.cells.set(board);
      this.state.set('lost');
      return;
    }
    this.flood(board, k);
    this.cells.set(board);
    if (board.filter((x) => !x.mine).every((x) => x.rev)) this.state.set('won');
  }

  flag(e: Event, k: number): void {
    e.preventDefault();
    this.flagCell(k);
  }
  private flagCell(k: number): void {
    const cells = this.cells();
    if (this.state() !== 'play' || cells[k].rev || !this.started) return;
    this.cells.update((cs) => cs.map((x, i) => (i === k ? { ...x, flag: !x.flag } : x)));
  }

  /** Chord: click a revealed number whose adjacent flags match its count to
   *  reveal the rest of its neighbours at once (hitting an unflagged mine loses). */
  chord(k: number): void {
    if (this.state() !== 'play') return;
    const cells = this.cells();
    const c = cells[k];
    if (!c.rev || c.adj === 0) return;
    const nb = this.neighbors(k);
    if (nb.filter((j) => cells[j].flag).length !== c.adj) return;
    const board = cells.map((x) => ({ ...x }));
    let boom = -1;
    for (const j of nb) {
      if (board[j].flag || board[j].rev) continue;
      if (board[j].mine) { boom = j; break; }
      this.flood(board, j);
    }
    if (boom >= 0) {
      board.forEach((x) => { if (x.mine) x.rev = true; });
      this.cells.set(board);
      this.state.set('lost');
      return;
    }
    this.cells.set(board);
    if (board.filter((x) => !x.mine).every((x) => x.rev)) this.state.set('won');
  }

  // ── pointer handling: tap = reveal/chord, hold (380ms) or right-click = flag ──
  private press: ReturnType<typeof setTimeout> | 'done' | null = null;
  onDown(k: number, e: MouseEvent | TouchEvent): void {
    if ('button' in e && e.button === 2) return;   // right-click flags via (contextmenu)
    this.press = setTimeout(() => { this.press = 'done'; this.flagCell(k); }, 380);
  }
  onUp(k: number, e: MouseEvent | TouchEvent): void {
    const held = this.press;
    if (held !== null && held !== 'done') clearTimeout(held);
    this.press = null;
    if (held === 'done') return;                    // long-press already flagged
    if ('button' in e && e.type === 'mouseup' && e.button !== 0) return;
    const c = this.cells()[k];
    if (c.rev) this.chord(k); else this.click(k);
  }
  onLeave(): void {
    if (this.press !== null && this.press !== 'done') clearTimeout(this.press);
  }
  onTouchEnd(k: number, e: TouchEvent): void {
    e.preventDefault();                             // avoid the synthetic mouse events
    this.onUp(k, e);
  }

  changeLevel(lv: LevelId): void {
    this.level.set(lv);
    this.cells.set(blank(lv));
    this.state.set('play');
    this.started = false;
  }

  reset(): void {
    this.cells.set(blank(this.level()));
    this.state.set('play');
    this.started = false;
  }
}
